import { formatBytes, hasStoragePlan } from 'helpers/storage';
import { classnames } from 'helpers/utils';
import styles from './vault-storage.module.scss';

interface VaultStorageProps {
  vault: { storageLimit: number; totalStorage: number };
  className?: string;
}

// How much of a vault's storage is used, e.g. "3.2 GB of 25 GB used" over a
// bar that turns amber near the limit and red when it's full. Vaults on the
// old subscriptions have no storage limit, so they show only what's used.
export const VaultStorage = ({ vault, className }: VaultStorageProps) => {
  const used = formatBytes(vault.totalStorage);

  if (!hasStoragePlan(vault)) {
    return (
      <div className={classnames(styles.storage, className ?? '')}>
        <span className="text__body__regular__small__gray700">
          {`${used} used · No limit`}
        </span>
      </div>
    );
  }

  const ratio = Math.min(vault.totalStorage / vault.storageLimit, 1);
  const percent = Math.round(ratio * 100);

  return (
    <div className={classnames(styles.storage, className ?? '')}>
      <span className="text__body__regular__small__gray700">
        {`${used} of ${formatBytes(vault.storageLimit)} used`}
      </span>
      <div
        className={styles.track}
        role="progressbar"
        aria-label="Storage used"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <div
          className={classnames(styles.fill, {
            [styles.fillWarning]: ratio >= 0.9 && ratio < 1,
            [styles.fillFull]: ratio >= 1,
          })}
          // A sliver stays visible for any usage, so a few MB don't read as empty.
          style={{
            width: `${vault.totalStorage > 0 ? Math.max(percent, 2) : 0}%`,
          }}
        />
      </div>
    </div>
  );
};
